import { createSelector, createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'react-fast-compare';
import { IWidgetsData, WidgetsLayouts } from '@models/widgets/IWidgetsData';
import { migrateWidgetsData } from '@models/widgets/migrateWidgetsData';
import { IWidgetConf } from '@models/widgets/IWidgetConf';
import { SLICE_NAMES } from '@/constants';
import type { RootState } from '@/store';

// Snapshot data вне Immer-draft: current() разворачивает draft, JSON-clone отрезает от
// будущих mutate-операций. structuredClone() здесь не работает — падает на Immer-proxy.
const snapshotData = (data: IWidgetsData): IWidgetsData =>
    JSON.parse(JSON.stringify(current(data))) as IWidgetsData;

const snapshotPayload = (data: IWidgetsData): IWidgetsData =>
    JSON.parse(JSON.stringify(data)) as IWidgetsData;

export interface IWidgetsState {
    isEdit: boolean;
    isLoading: boolean;
    data: IWidgetsData;
    // Snapshot data на момент входа в режим редактирования. По нему сравниваем dirty-state.
    originalData: IWidgetsData | null;
    // Последний known-clean snapshot, синхронизированный с сервером (load/save/autosave).
    // Baseline для dirty-detection берётся из него, а не из state.data — иначе грязные
    // изменения, оставшиеся после выхода из edit без save/cancel (например, через
    // toggle в контекстном меню), станут ложным baseline и dirty потеряется.
    lastSavedData: IWidgetsData | null;
}

const initialState: IWidgetsState = {
    isEdit: false,
    isLoading: true,
    data: {
        widgetIds: [] as string[],
        layouts: {},
        widgetConfigurations: {},
    } as IWidgetsData,
    originalData: null,
    lastSavedData: null,
};

export const WidgetsSlice = createSlice({
    name: SLICE_NAMES.WIDGETS,
    initialState,
    reducers: {
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setIsEdit: (state, action: PayloadAction<boolean>) => {
            state.isEdit = action.payload;
            // Baseline всегда из last-known-clean (server snapshot). Если lastSavedData ещё
            // не было — fallback на текущий state.data (сценарий до первого load).
            state.originalData = action.payload
                ? state.lastSavedData
                    ? snapshotPayload(state.lastSavedData)
                    : snapshotData(state.data)
                : null;
        },
        successLoadWidgetsData: (state, action: PayloadAction<IWidgetsData>) => {
            // Конвертируем раскладку из старой 12-колоночной сетки в новую квадратную, если нужно.
            const data = migrateWidgetsData(action.payload);
            state.data = data;
            state.lastSavedData = snapshotPayload(data);
            // Если в этот момент isEdit=true (например, mount Dashboard сразу в edit-режиме),
            // baseline тоже привязываем к свежим серверным данным.
            if (state.isEdit) {
                state.originalData = snapshotPayload(data);
            }
        },
        setLayouts: (state, action: PayloadAction<WidgetsLayouts>) => {
            state.data.layouts = action.payload;
        },
        addWidgetWithLayout: (
            state,
            action: PayloadAction<{ conf: IWidgetConf; layouts: WidgetsLayouts }>,
        ) => {
            const { conf, layouts } = action.payload;
            state.data.widgetIds.push(conf.widgetId);
            state.data.widgetConfigurations[conf.widgetId] = conf.configuration;
            state.data.layouts = layouts;
        },
        removeWidgets: (state, action: PayloadAction<string>) => {
            state.data.widgetIds = state.data.widgetIds.filter((widgetId) => widgetId !== action.payload);
            delete state.data.widgetConfigurations[action.payload];
        },
        setWidgetConfiguration: (state, action: PayloadAction<IWidgetConf>) => {
            state.data.widgetConfigurations[action.payload.widgetId] = action.payload.configuration;
        },
        // После успешной синхронизации с сервером (saveDashboard / addWidgetsAndSave) фиксируем
        // новый чистый baseline: и lastSavedData, и originalData в edit-режиме.
        commitBaseline: (state) => {
            const snap = snapshotData(state.data);
            state.lastSavedData = snap;
            if (state.isEdit) {
                state.originalData = snap;
            }
        },
    },
});

export const {
    setIsLoading,
    setIsEdit,
    setLayouts,
    successLoadWidgetsData,
    removeWidgets,
    setWidgetConfiguration,
} = WidgetsSlice.actions;

const selectWidgetsData = (s: RootState) => s.widgets.data;
const selectOriginalData = (s: RootState) => s.widgets.originalData;

// react-grid-layout после mount обогащает layout-item undefined-полями (isDraggable, resizeHandles, …),
// которые JSON.stringify (использованный для snapshot) отбрасывает. Чтобы dirty-state не загорался
// сразу при входе в edit, сравниваем обе стороны как JSON-нормализованные.
export const selectIsDashboardDirty = createSelector(
    [selectWidgetsData, selectOriginalData],
    (data, original) => {
        if (original === null) return false;
        const normalized = JSON.parse(JSON.stringify(data));
        return !isEqual(normalized, original);
    },
);

export default WidgetsSlice.reducer;
